import { envConfig } from '../config/authConfig';
import {
  CvAnalysisResult,
  CvUpdateStats,
  UserCvOverview,
  UserCvVersion,
} from '../types/userCv';

const BASE = envConfig.apiBaseUrl;

type ApiCall = (url: string, options?: RequestInit) => Promise<Response>;
type GetToken = () => Promise<string | null>;

async function unwrap<T>(res: Response): Promise<T> {
  const text = await res.text();
  let json: any = null;
  try { json = text ? JSON.parse(text) : null; } catch { /* non-JSON body */ }

  if (!res.ok) {
    const msg = json?.errors?.[0] ?? json?.message ?? json?.title
      ?? (text ? text.slice(0, 200) : `Request failed (${res.status})`);
    throw new Error(msg);
  }
  if (!json || json.success === false) {
    throw new Error(json?.errors?.[0] ?? json?.message ?? 'Request failed');
  }
  return json.data as T;
}

export async function getCvOverview(employeeId: string, apiCall: ApiCall): Promise<UserCvOverview> {
  const res = await apiCall(`${BASE}/api/usercv/${encodeURIComponent(employeeId)}`);
  return unwrap<UserCvOverview>(res);
}

export async function getCvUpdateStats(
  employeeId: string,
  connectId: number,
  apiCall: ApiCall
): Promise<CvUpdateStats> {
  const res = await apiCall(
    `${BASE}/api/usercv/${encodeURIComponent(employeeId)}/by-connect/${connectId}`
  );
  return unwrap<CvUpdateStats>(res);
}

/**
 * Upload a CV file (PDF or DOCX) using multipart/form-data.
 * Bypasses the json `apiCall` so we can set the multipart Content-Type.
 */
export async function uploadCv(
  employeeId: string,
  file: File,
  uploadedBy: string,
  getToken: GetToken
): Promise<UserCvVersion> {
  const token = await getToken();
  const fd = new FormData();
  fd.append('file', file);
  fd.append('uploadedBy', uploadedBy);

  const res = await fetch(`${BASE}/api/usercv/${encodeURIComponent(employeeId)}/upload`, {
    method: 'POST',
    body: fd,
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  return unwrap<UserCvVersion>(res);
}

/** Returns a downloadable URL with auth token applied. Caller is expected to fetch + open. */
export async function downloadCv(
  employeeId: string,
  version: number,
  format: 'original' | 'docx',
  getToken: GetToken
): Promise<{ blob: Blob; fileName: string }> {
  const token = await getToken();
  const url = `${BASE}/api/usercv/${encodeURIComponent(employeeId)}/download/${version}${
    format === 'docx' ? '?format=docx' : ''
  }`;
  const res = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  if (!res.ok) throw new Error(`Download failed (${res.status})`);
  const blob = await res.blob();
  // Try to read filename from Content-Disposition
  const cd = res.headers.get('Content-Disposition') || '';
  const m = /filename="?([^";]+)"?/i.exec(cd);
  return { blob, fileName: m?.[1] ?? `cv-v${version}.${format === 'docx' ? 'docx' : 'pdf'}` };
}

export async function analyzeFromConnect(
  employeeId: string,
  connectId: number,
  apiCall: ApiCall
): Promise<CvAnalysisResult> {
  const res = await apiCall(
    `${BASE}/api/usercv/${encodeURIComponent(employeeId)}/analyze-from-connect/${connectId}`,
    { method: 'POST' }
  );
  return unwrap<CvAnalysisResult>(res);
}

/** Analyze multiple completed connects in one combined AI call. */
export async function analyzeFromConnects(
  employeeId: string,
  connectIds: number[],
  apiCall: ApiCall
): Promise<CvAnalysisResult> {
  const res = await apiCall(
    `${BASE}/api/usercv/${encodeURIComponent(employeeId)}/analyze-from-connects`,
    {
      method: 'POST',
      body: JSON.stringify({ connectIds }),
    }
  );
  return unwrap<CvAnalysisResult>(res);
}

export async function applyCvUpdate(
  employeeId: string,
  connectIdOrIds: number | number[],
  analysis: CvAnalysisResult,
  apiCall: ApiCall
): Promise<UserCvVersion> {
  const ids = Array.isArray(connectIdOrIds) ? connectIdOrIds : [connectIdOrIds];
  const res = await apiCall(
    `${BASE}/api/usercv/${encodeURIComponent(employeeId)}/apply-update`,
    {
      method: 'POST',
      body: JSON.stringify({
        connectId: ids[0],
        connectIds: ids,
        analysis,
      }),
    }
  );
  return unwrap<UserCvVersion>(res);
}

/** Trigger a browser download for a Blob. */
export function triggerBrowserDownload(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 200);
}
